import Immutable from 'immutable';

import LogEntry from 'sharedreact/data/LogEntry';
import StringSupport from 'sharedreact/utils/StringSupport';

const EVENT_TIMEOUT_MILLIS = 30000;

let _unauthorizedHandler: (() => void) | undefined;

function getUnauthorizedHandler () {
  return _unauthorizedHandler;
}

class SocketEventListener {
  private openedHandler: (event: Event | string) => void;
  private errorHandler: (event: Event | string) => void;
  private listeners: Immutable.Map<string, (name: string, data: string) => void>;
  private socketPath: string;
  private sessionTestPath: string | null;
  private socket: WebSocket | null;
  private lastEventReceivedMillis: number;
  private restartsCount: number;
  private didClose: boolean;
  private explicitClose: boolean;

  private logger: ((logEntry: LogEntry) => void) | null;

  private monitorSocketId: number | undefined;

  private didRestartCallback: (() => void) | undefined;
  
  constructor () {
    this.monitorSocket = this.monitorSocket.bind(this);
    this.openedHandler = () => {};
    this.errorHandler = () => {};
    this.listeners = Immutable.Map();
    
    this.handleOpened = this.handleOpened.bind(this);
    this.handleEvent = this.handleEvent.bind(this);
    this.handleClose = this.handleClose.bind(this);

    this.socketPath = '';
    this.sessionTestPath = null;
    this.logger = null;
    this.socket = null;
    
    this.monitorSocketId = undefined;
    this.lastEventReceivedMillis = 0;
    this.restartsCount = 0;
    this.didClose = false;
    this.explicitClose = false;
  }

  init (
    openedHandler: (event: Event | string) => any,
    errorHandler: (event: Event | string) => any,
    subPath: string,
    serverName: string = window.location.hostname,
    port: string = window.location.port,
    protocol: string = window.location.protocol,
    didRestartCallback?: () => void,
    sessionTestPath?: string | null
  ) {
    this.openedHandler = openedHandler;
    this.errorHandler = errorHandler;
    this.socketPath = `${protocol === 'https:' ? 'wss:' : 'ws:'}//${serverName}${(port ? ':' + port : '')}/${subPath}`;
    this.sessionTestPath = (
      sessionTestPath === null
      ? null
      : `${protocol}//${serverName}${(port ? ':' + port : '')}/${sessionTestPath != null ? sessionTestPath : 'services/isSessionStillValid'}`
    );
    this.listeners = Immutable.Map();

    this.didRestartCallback = didRestartCallback;
  }

  setUnauthorizedHandler (handler: () => void): void {
    _unauthorizedHandler = handler;
  }

  setLogger (logger: ((logEntry: LogEntry) => void) | null): void {
    this.logger = logger;
  }

  addJSONListener (eventName: string, listener: (data: any) => void) {
    this.addListener(eventName, (name: string, data: any) => {
      listener(data);
    });
  }

  addAllEventsJSONListener (listener: (name: string, data: any) => void) {
    this.addListener('*', listener);
  }

  start () {
    this.lastEventReceivedMillis = new Date().getTime();

    this.close();

    this.socket = new WebSocket(this.socketPath);
    this.socket.onerror = this.errorHandler;
    this.socket.onopen = this.handleOpened;
    this.socket.onclose = this.handleClose;

    this.monitorSocketId = window.setInterval(this.monitorSocket, 3000);

    this.socket!.addEventListener('message',  this.handleEvent);
  }

  private cleanUpAfterClose () {
    if (this.monitorSocketId != null) {
      window.clearInterval(this.monitorSocketId);
    }

    if (this.socket != null) {
      this.socket.removeEventListener('message', this.handleEvent);
      this.socket = null;
    }
  }

  close (): void {
    if (this.socket != null) {
      this.explicitClose = true;
      if (this.monitorSocketId != null) {
        window.clearInterval(this.monitorSocketId);
      }

      this.socket.removeEventListener('message', this.handleEvent);
      this.socket.close();

      this.socket = null;
    }
  }

  private restart (doClose: boolean): void {
    // restart the Web Socket
    ++this.restartsCount;
    if (doClose) {
      this.close();
    } else {
      this.cleanUpAfterClose();
    }
    this.start();
  }

  private handleClose (event: CloseEvent) : void {
    if (!this.explicitClose) {
      this.didClose = true;
      if (this.restartsCount < 5) {
        if (this.sessionTestPath !== null) {
          console.log('check authorized');
          
          $.ajax(this.sessionTestPath, { type: 'post' }).done((result) => {
            this.errorHandler('connection lost');
            this.restart(false);
          }).fail((jqXHR, textStatus, errorThrown) => {
            if (jqXHR.status == 401) {
              this.cleanUpAfterClose();
              const unauthorizedHandler = getUnauthorizedHandler();
              if (unauthorizedHandler != null) {
                unauthorizedHandler();
              }
            } else {
              this.restart(false);
            }
          });
        } else {
          this.restart(false);
        }
      } else {
        this.errorHandler('restore events connection failed, refresh browser manually');
        if (this.monitorSocketId != null) {
          window.clearInterval(this.monitorSocketId);
          this.monitorSocketId = undefined;
        }
      }
    }

    this.explicitClose = false;
  }

  private monitorSocket (): void {
    if (this.socket != null) {
      const socket = this.socket;
      const eventTimeoutMillis: number = this.lastEventReceivedMillis + EVENT_TIMEOUT_MILLIS;

      const currentTime: number = new Date().getTime();

      if (socket.readyState === 1) {
        if (this.didClose) {
          this.didClose = false;
          if (this.didRestartCallback != null) {
            this.didRestartCallback();
          }
        }

        if (currentTime > eventTimeoutMillis) {
          if (this.restartsCount < 5) {
            this.errorHandler(
              'no events received for >' + EVENT_TIMEOUT_MILLIS / 1000 + 's'
            );

            this.restart(true);
          } else {
            this.errorHandler('restore events connection failed, refresh browser manually');
            this.close();
          }
        }
      }
    }
  }
  
  private handleOpened (event: Event): any {
    this.lastEventReceivedMillis = new Date().getTime();
    this.openedHandler(event);
  }

  private handleEvent (event: MessageEvent): void {
    if (event.data != null) {
      const data: string = event.data;
      const eventNameSepPos: number = data.indexOf(':');

      if (eventNameSepPos !== -1) {
        this.restartsCount = 0;
        this.lastEventReceivedMillis = new Date().getTime();

        const dataObj: {[key: string]: any} = JSON.parse(data);

        const listener: ((name: string, data: string) => void) | undefined = this.listeners.get(dataObj.name);
        if (listener != null) {
          listener(dataObj.name, dataObj.data);
        }

        const allEventsListener: ((name: string, data: string) => void) | undefined = this.listeners.get('*');
        if (allEventsListener != null) {
          allEventsListener(dataObj.name, dataObj.data);
        }

        if (this.logger != null) {
          const logger: (logEntry: LogEntry) => void = this.logger;

          const details: {[key: string]: any} = {
            name: dataObj.name,
            data: dataObj.data
          };

          const time = new Date();

          logger(new LogEntry({
            time: time,
            logLevel: LogEntry.LogLevel.trace,
            type: 'event',
            message: StringSupport.replaceArgs('event {0}: {1}', details.name, details.data.toString()),
            details: details
          }));
        }
      }
    }
  }

  private addListener (eventName: string, listener: (name: string, data: string) => void) {
    this.listeners = this.listeners.set(eventName, listener);
  }
}

export default new SocketEventListener();